import { useMemo, useState } from "react";
import { Navigate } from "react-router";
import { Alert, Box, Button, CircularProgress, Paper, Stack, TextField, Typography } from "@mui/material";
import { useAuth } from "src/context/AuthContext";
import { useLang, useT } from "src/i18n";
import { resolveErrorMessage } from "src/i18n/errorCopy";
import { useNavSignals } from "src/hooks/useNavSignals";
import { applyJerry } from "src/utils/liity/applyJerryApi";
import { captureError } from "src/utils/errorReporting";

// Operator self-application screen. Shown by HomeResolver when the active
// company is an operator (isPumppuToimittaja) but not yet Jerry-activated.
export default function LiityJerry() {
    const { token, user, ownerAsiakasId, logout } = useAuth();
    const { lang } = useLang();
    const t = useT();
    const { applicationStatus, loading, role, operatorNeedsActivation } = useNavSignals();

    const person = user?.person?.[0];
    const companyName = useMemo(
        () => (user?.asiakasList || []).find((a) => a.asiakasId === ownerAsiakasId)?.asiakasNimi || null,
        [user, ownerAsiakasId]
    );

    const [form, setForm] = useState({ alue: "", coverageRadiusKm: "", phone: person?.personPhone || "", note: "" });
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle"); // idle | submitting | done | error
    const [serverError, setServerError] = useState("");

    const update = (key) => (e) => {
        const value = e.target.value;
        setForm((f) => ({ ...f, [key]: value }));
        setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
    };

    const validate = (f) => {
        const errs = {};
        if (!f.alue.trim()) errs.alue = t("provider.liity.areaRequiredError");
        if (!f.phone.trim()) errs.phone = t("provider.liity.phoneRequiredError");
        if (f.coverageRadiusKm !== "" && !(Number(f.coverageRadiusKm) >= 0)) errs.coverageRadiusKm = t("provider.liity.coverageInvalidError");
        return errs;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const errs = validate(form);
        setErrors(errs);
        if (Object.keys(errs).length > 0) return;
        setStatus("submitting");
        setServerError("");
        try {
            await applyJerry({
                token,
                alue: form.alue.trim(),
                coverageRadiusKm: form.coverageRadiusKm === "" ? null : Number(form.coverageRadiusKm),
                phone: form.phone.trim(),
                note: form.note.trim(),
            });
            setStatus("done");
        } catch (err) {
            if (err.status === 401) { logout(); return; }
            setStatus("error");
            // err.body?.message and err.message are the same value here — apiFetch's
            // jsonOrThrow already sets err.message from body.message.
            setServerError(resolveErrorMessage(err, { t, lang, fallbackKey: "jerry.applyFailed" }));
            captureError(err, { tags: { feature: "jerry", operation: "self-apply" } });
        }
    };

    // Direct-URL / stale-bookmark guard: HomeResolver only runs on "/", so an
    // already-activated operator can land here by typing /liity or reloading.
    // Nothing to join — send them to the provider inbox (mirrors homePathFor).
    // Wait out the nav probe first so we don't bounce mid-load on a stale null.
    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }
    if (role === "operator" && !operatorNeedsActivation) {
        return <Navigate to="/tarjouspyynnot" replace />;
    }

    if (status === "done") {
        return (
            <Box sx={{ maxWidth: 560, mx: "auto", py: 4 }}>
                <Alert severity="success" sx={{ mb: 2 }}>{t("provider.liity.doneAlert")}</Alert>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {t("provider.liity.doneHint")}
                </Typography>
            </Box>
        );
    }

    return (
        <Box component="form" noValidate onSubmit={onSubmit} sx={{ maxWidth: 560, mx: "auto", py: 4 }}>
            <Typography variant="h5" gutterBottom>{t("provider.liity.heading")}</Typography>
            <Typography variant="body1" sx={{ color: "text.secondary", mb: 2 }}>
                {companyName
                    ? t("provider.liity.introPrefixWithCompany", { company: companyName })
                    : t("provider.liity.introPrefixNoCompany")}
                {t("provider.liity.introBody")}
            </Typography>

            {applicationStatus && status !== "done" && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {t("provider.liity.alreadyPendingNotice")}
                </Alert>
            )}
            {status === "error" && <Alert severity="error" sx={{ mb: 2 }}>{serverError}</Alert>}

            <Paper variant="outlined" sx={{ p: 2.5 }}>
                <Stack spacing={2}>
                    <TextField
                        label={t("provider.liity.areaFieldLabel")} value={form.alue} onChange={update("alue")}
                        error={!!errors.alue} helperText={errors.alue || t("provider.liity.areaFieldHelper")} fullWidth required
                    />
                    <TextField
                        label={t("provider.liity.coverageFieldLabel")} type="number" value={form.coverageRadiusKm} onChange={update("coverageRadiusKm")}
                        error={!!errors.coverageRadiusKm} helperText={errors.coverageRadiusKm || t("provider.liity.coverageFieldHelper")}
                        slotProps={{ htmlInput: { min: 0 } }} fullWidth
                    />
                    <TextField
                        label={t("provider.liity.phoneFieldLabel")} value={form.phone} onChange={update("phone")}
                        error={!!errors.phone} helperText={errors.phone} fullWidth required
                    />
                    <TextField
                        label={t("provider.liity.noteFieldLabel")} value={form.note} onChange={update("note")}
                        multiline minRows={2} fullWidth
                    />
                    <Button type="submit" variant="contained" size="large" disabled={status === "submitting"}>
                        {status === "submitting" ? t("provider.liity.submitting") : t("provider.liity.submitCta")}
                    </Button>
                </Stack>
            </Paper>
        </Box>
    );
}
